<template>
    <!-- Loading overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center" persistent>
        <v-progress-circular color="amber" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!--== Add an Event Form ==-->
    <article>
        <h1 class="text-center m-5">ADD EVENTS</h1>
        <!-- Set novalidate because of custom validations -->
        <form class="col-sm-6 mx-auto align-self-center align-items-center bg-body-tertiary shadow rounded-4 py-5"
            @submit.prevent="submitAddForm" novalidate>
            <!-- No image uploaded -->
            <div class="row justify-content-center mx-1" v-if="!uploadedEventPhotoUrl">
                <label for="eventPhoto"
                    class="col-sm-3 col-form-label col-form-label-lg fw-bold my-auto text-sm-center">Event Photo</label>
                <div class="col-sm-8 my-auto d-grid">
                    <label for="eventPhoto" class="btn btn-primary fw-semibold">Upload</label>

                    <input type="file" @change="
                        onFileChange($event);
                    previewPhotoUrl($event);
                    " class="form-control d-none" id="eventPhoto" accept="image/*" />
                    <div v-for="error in addForm$.eventPhoto.$errors" :key="error.$uid" class="alert alert-danger p-1">
                        <em> {{ error.$message }} </em>
                    </div>
                </div>
            </div>

            <!-- Has image -->
            <div class="row justify-content-center mx-1" v-else>
                <label for="eventPhoto"
                    class="col-sm-3 col-form-label col-form-label-lg fw-bold my-auto text-sm-center">Event Photo</label>
                <div class="col-sm-8 my-auto d-grid">
                    <span>
                        <a class="form-text" :href="uploadedEventPhotoUrl" target="_blank">
                            {{ uploadedEventPhoto.name }}</a>
                        <button type="button" class="btn btn-tertiary">
                            <font-awesome-icon icon="fa fa-xmark" @click="
                                deleteEventPhotoUrl();
                            deleteFile();
                            " class="fa-fw" />
                        </button>
                        <div v-for="error in addForm$.eventPhotoTitle.$errors" :key="error.$uid"
                            class="alert alert-danger p-1">
                            <em> {{ error.$message }} </em>
                        </div>
                    </span>
                    <!-- Delete photo -->
                </div>
            </div>

            <!-- Title -->
            <div class="row justify-content-center mx-1">
                <label for="eventTitle" class="col-sm-3 col-form-label col-form-label-lg fw-bold my-auto text-sm-center">
                    Title
                </label>
                <div class="col-sm-8 my-auto">
                    <input type="text" class="form-control" id="eventTitle" placeholder="Enter a title"
                        v-model="addFormData.eventTitle" @input="addForm$.eventTitle.$touch" />
                    <div v-if="addForm$.eventTitle.$errors[0]" class="alert alert-danger m-0 p-1 mb-3">
                        <em> {{ addForm$.eventTitle.$errors[0].$message }}</em>
                    </div>
                </div>
            </div>

            <!-- Category -->
            <div class="row justify-content-center mx-1">
                <label for="eventCategory" class="col-sm-3 col-form-label col-form-label-lg fw-bold my-auto text-sm-center">
                    Category
                </label>
                <div class="col-sm-8 my-auto">
                    <input type="text" class="form-control" id="eventCategory" placeholder="Enter a category"
                        v-model="addFormData.eventCategory" @input="addForm$.eventCategory.$touch" />
                    <div v-if="addForm$.eventCategory.$errors[0]" class="alert alert-danger m-0 p-1 mb-3">
                        <em> {{ addForm$.eventCategory.$errors[0].$message }}</em>
                    </div>
                </div>
            </div>

            <!-- Schedule -->
            <div class="row justify-content-center mx-1">
                <label for="eventTime" class="col-sm-3 col-form-label col-form-label-lg fw-bold my-auto text-sm-center">
                    Schedule
                </label>
                <div class="col-sm-8 my-auto">
                    <input type="datetime-local" class="form-control" id="eventTime" v-model="addFormData.eventTime"
                        @input="addForm$.eventTime.$touch" />
                    <div v-if="addForm$.eventTime.$errors[0]" class="alert alert-danger m-0 p-1 mb-3">
                        <em> {{ addForm$.eventTime.$errors[0].$message }}</em>
                    </div>
                </div>
            </div>

            <!-- Platform -->
            <div class="row justify-content-center mx-1">
                <label for="eventPlatform" class="col-sm-3 col-form-label col-form-label-lg fw-bold my-auto text-sm-center">
                    Platform
                </label>
                <div class="col-sm-8 my-auto">
                    <select class="form-select" id="eventPlatform" v-model="addFormData.eventPlatform">
                        <option value="" selected disabled hidden>Choose here</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Zoom">Zoom</option>
                        <option value="MS Teams">MS Teams</option>
                        <option value="Kumu">Kumu</option>
                        <option value="YouTube">YouTube</option>
                        <option value="Google Meet">Google Meet</option>
                        <option value="Discord">Discord</option>
                        <option value="Twitch">Twitch</option>
                        <option value="Kick">Kick</option>
                    </select>
                    <div v-if="addForm$.eventPlatform.$errors[0]" class="alert alert-danger m-0 p-1 mb-2">
                        <em> {{ addForm$.eventPlatform.$errors[0].$message }}</em>
                    </div>
                </div>
            </div>

            <!-- Description -->
            <div class="row justify-content-center mx-1">
                <label for="eventDescription"
                    class="col-sm-3 col-form-label col-form-label-lg fw-bold my-auto text-sm-center">
                    Description
                </label>
                <div class="col-sm-8 my-auto mt-1">
                    <textarea class="form-control" id="eventDescription" v-model="addFormData.eventDescription"
                        placeholder="Enter a description" @input="addForm$.eventDescription.$touch"></textarea>
                    <div v-if="addForm$.eventDescription.$errors[0]" class="alert alert-danger m-0 p-1">
                        <em> {{ addForm$.eventDescription.$errors[0].$message }}</em>
                    </div>
                </div>
            </div>

            <!-- Tooltip hover -->
            <div class="d-grid gap-2 d-sm-block text-sm-end mt-3 mx-5">
                <v-tooltip text="Note: New events are closed by default" location="bottom">
                    <template v-slot:activator="{ props }">
                        <button v-bind="props" type="submit" class="btn btn-primary fw-semibold text-white mx-5" style="
                --bs-btn-padding-y: 0.25rem;
                --bs-btn-padding-x: 1.5rem;
                --bs-btn-font-size: 1rem;
              ">
                            Create
                        </button>
                    </template>
                </v-tooltip>
            </div>
        </form>
    </article>
</template>

<script>
import { ref, reactive, computed } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import {
    getStorage,
    ref as storageRef,
    uploadBytesResumable,
} from "@firebase/storage";
import {
    collection,
    addDoc,
    serverTimestamp,
    query,
    where,
    getDocs,
    Timestamp,
} from "@firebase/firestore";
import { db, uploadToStorage } from "@/firebase";
import useVuelidate from "@vuelidate/core";
import { required, helpers, maxLength } from "@vuelidate/validators";
const { withAsync } = helpers;
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { v4 as uuidv4 } from "uuid";

export default {
    data() {
        return {
            uploadedEventPhoto: null, // Image file
            uploadedEventPhotoUrl: null, // Image URL
        };
    },
    setup() {
        const router = useRouter();
        const storage = getStorage();
        const user = computed(() => useStore().getters["auth/user"]);
        const email = user.value.email; // For created by field

        // Form data for add
        const addFormData = reactive({
            eventPhoto: "",
            eventPhotoTitle: "",
            eventTitle: "",
            eventCategory: "",
            eventTime: "",
            eventPlatform: "",
            eventDescription: "",
        });

        // Custom rule for category (must be unique)
        const categoryExists = withAsync(async (value) => {
            const events = [];

            // Get of all unarchived events
            const q = query(
                collection(db, "Events"),
                where("IsArchived", "==", false)
            );

            // Convert category to lowercase
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
                events.push(doc.data().Category.toLowerCase());
            });

            // Check if category already exists
            return !events.includes(value.toLowerCase());
        });

        // Custom rule for time
        const isNotBeforePresent = (value) => {
            // Check if time is not before present
            return new Date(value) >= new Date();
        };

        const isAcceptedImageFileExtension = (value) => {
            const acceptedExtensions = [
                "xbm",
                "tif",
                "jfif",
                "ico",
                "tiff",
                "gif",
                "svg",
                "jpeg",
                "svgz",
                "jpg",
                "webp",
                "png",
                "bmp",
                "pjp",
                "apng",
                "pjpeg",
                "avif",
            ];
            const fileExtension = value.split(".").pop();
            return acceptedExtensions.includes(fileExtension);
        };

        // Vuelidate rules
        const addFormRules = {
            eventPhoto: {
                required: helpers.withMessage("An image is required", required),
            },
            eventPhotoTitle: {
                isAcceptedFileExtension: helpers.withMessage(
                    "The file must be an image",
                    isAcceptedImageFileExtension
                ),
                maxLength: helpers.withMessage(
                    "The maximum file title length allowed is 150",
                    maxLength(150)
                ),
            },
            eventTitle: { required, maxLength: maxLength(100) },
            eventCategory: {
                required,
                categoryExists: helpers.withMessage(
                    "The category already exists",
                    categoryExists
                ),
                maxLength: maxLength(25),
            },
            eventPlatform: { required },
            eventTime: {
                required,
                isNotBeforePresent: helpers.withMessage(
                    "The event time must be in the future",
                    isNotBeforePresent
                ),
            },
            eventDescription: { required, maxLength: maxLength(500) },
        };

        // Vuelidate init
        const addForm$ = useVuelidate(addFormRules, addFormData);

        // Overlay handling
        const loading = ref(false);

        // File validation and event listeners
        const onFileChange = (event) => {
            addFormData.eventPhoto = event.target.files[0];
            addFormData.eventPhotoTitle = event.target.files[0]
                ? event.target.files[0].name
                : null;
        };
        const deleteFile = () => {
            addFormData.eventPhoto = null;
            addFormData.eventPhotoTitle = null;
        };

        // Submit
        const submitAddForm = async () => {
            // Show loading overlay
            loading.value = true;

            // Validate time and category again because they are sensitive to changes
            const eventTimeForceValidate = addForm$.value.eventTime.$model;
            addForm$.value.eventTime.$model = null;
            addForm$.value.eventTime.$model = eventTimeForceValidate;
            const eventCategoryForceValidate = addForm$.value.eventCategory.$model;
            addForm$.value.eventCategory.$model = null;
            addForm$.value.eventCategory.$model = eventCategoryForceValidate;

            const result = await addForm$.value.$validate();

            // If no errors
            if (result) {
                addForm$.value.$reset();

                // Upload event photo
                const storageEventPhotoRef = storageRef(
                    storage,
                    "Event Photos/" + uuidv4() + addFormData.eventPhoto.name
                );
                const eventPhotoUploadTask = uploadBytesResumable(
                    storageEventPhotoRef,
                    addFormData.eventPhoto
                );
                const eventPhotoDownloadUrl = await uploadToStorage(
                    eventPhotoUploadTask
                );

                const colRef = collection(db, "Events");
                await addDoc(colRef, {
                    ArchiveStatus: "",
                    Category: addFormData.eventCategory,
                    CreatedBy: email,
                    CreationTimestamp: serverTimestamp(),
                    Description: addFormData.eventDescription,
                    IsArchived: false,
                    IsOpen: false,
                    MainHost: "",
                    MainHostName: "",
                    ModifiedBy: "",
                    ModifiedTimestamp: "",
                    Name: addFormData.eventTitle,
                    Platform: addFormData.eventPlatform,
                    StartTime: Timestamp.fromDate(new Date(addFormData.eventTime)),
                    ThumbnailUrl: eventPhotoDownloadUrl,
                    ThumbnailUrlFileName: addFormData.eventPhoto.name,
                });

                toast.success("Event successfully added", {
                    autoClose: 2000,
                });

                router.push("/events");
            } else {
                toast.error("Unable to add event");
            }

            // Hide loading overlay
            loading.value = false;
        };

        return {
            addFormData,
            addForm$,
            submitAddForm,
            onFileChange,
            deleteFile,
            loading,
        };
    },
    methods: {
        previewPhotoUrl(event) {
            this.uploadedEventPhoto = event.target.files[0];
            this.uploadedEventPhotoUrl = URL.createObjectURL(event.target.files[0]);
        },
        deleteEventPhotoUrl() {
            this.uploadedEventPhoto = null;
            this.uploadedEventPhotoUrl = null;
        },
    },
};
</script>

<style scoped src="@/assets/css/admin.css"></style>
